import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, Webhook } from '@mui/icons-material';
import { DynamicDataGrid } from '../common/DynamicContainer';
import { toast } from 'react-toastify';
import {
  getAuthHeaders,
  MASTER_CONNECTOR_API,
  normalizeName,
  webhookApiBase,
} from './webhookApi';

const emptyForm = () => ({
  mc_id: '',
  application_name: '',
  event_name: '',
  event_description: '',
  enabled: true,
});

const WebhookEventRegistryAdmin = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [masterConnectors, setMasterConnectors] = useState([]);
  const [registry, setRegistry] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const loadMasterConnectors = useCallback(async () => {
    try {
      const res = await fetch(MASTER_CONNECTOR_API, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setMasterConnectors(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setMasterConnectors([]);
    }
  }, []);

  const loadRegistry = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${webhookApiBase()}/registry`, { headers: getAuthHeaders() });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setRegistry(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to load webhook event registry');
      setRegistry([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMasterConnectors();
    loadRegistry();
  }, [loadMasterConnectors, loadRegistry]);

  const handleMcSelect = (mcId) => {
    const mc = masterConnectors.find((c) => c.mcId === mcId);
    setForm((f) => ({
      ...f,
      mc_id: mcId,
      application_name: mc ? normalizeName(mc.mcName || '') : '',
    }));
  };

  const openCreateDialog = () => {
    setEditingId(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEditDialog = (row) => {
    setEditingId(row.id);
    setForm({
      mc_id: row.mc_id || '',
      application_name: row.application_name || '',
      event_name: row.event_name || '',
      event_description: row.event_description || '',
      enabled: row.enabled !== false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.application_name.trim() || !form.event_name.trim()) {
      toast.error('Application and event name are required');
      return;
    }
    setSaving(true);
    try {
      const body = {
        mc_id: form.mc_id || null,
        application_name: normalizeName(form.application_name),
        event_name: normalizeName(form.event_name),
        event_description: form.event_description || null,
        source_type: 'webhook',
        enabled: form.enabled,
      };
      const url = editingId
        ? `${webhookApiBase()}/registry/${encodeURIComponent(editingId)}`
        : `${webhookApiBase()}/registry`;
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      toast.success(editingId ? 'Event registry entry updated' : 'Event registry entry created');
      setDialogOpen(false);
      setEditingId(null);
      setForm(emptyForm());
      await loadRegistry();
    } catch (e) {
      toast.error(e.message || 'Failed to save registry entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!row?.id) return;
    setSaving(true);
    try {
      const res = await fetch(`${webhookApiBase()}/registry/${encodeURIComponent(row.id)}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      toast.success('Event registry entry deleted');
      setDeleteConfirm(null);
      await loadRegistry();
    } catch (e) {
      toast.error(e.message || 'Failed to delete registry entry');
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(
    () => [
      { field: 'application_name', headerName: 'Application', width: 140 },
      { field: 'event_name', headerName: 'Event', width: 160 },
      { field: 'event_description', headerName: 'Description', width: 280 },
      {
        field: 'enabled',
        headerName: 'Active',
        width: 80,
        renderCell: (params) => (
          <Chip size="small" label={params.value ? 'Yes' : 'No'} color={params.value ? 'success' : 'default'} />
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 100,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => openEditDialog(params.row)}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={() => setDeleteConfirm(params.row)}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [],
  );

  const rows = registry.map((r) => ({ id: r.id, ...r }));

  return (
    <>
      <Paper sx={{ m: 1, p: 2, minHeight: 'calc(100vh - 120px)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Webhook color="primary" />
          <Typography variant="h6">Webhook Event Registry</Typography>
          {loading && <CircularProgress size={20} />}
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          Platform-wide catalog (CDX). Define which inbound event types exist per application.
          All vendors (QuickBooks HTTP webhooks, Gmail Pub/Sub push, etc.) use the same ingress:
          POST /webhooks/inbound. No Global Tools or MCP setup required here.
          Tenants configure subscriptions and routes under Sync Flows → Webhooks.
        </Alert>

        <Box sx={{ mb: 2 }}>
          <Button variant="contained" startIcon={<Add />} onClick={openCreateDialog}>
            Add Event
          </Button>
        </Box>

        <DynamicDataGrid rows={rows} columns={columns} hideFooter />
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Webhook Event (Global)' : 'Add Webhook Event (Global)'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Application (master connector)</InputLabel>
            <Select label="Application (master connector)" value={form.mc_id} onChange={(e) => handleMcSelect(e.target.value)}>
              <MenuItem value=""><em>Manual</em></MenuItem>
              {masterConnectors.map((mc) => (
                <MenuItem key={mc.mcId} value={mc.mcId}>{mc.mcName}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField label="Application name" value={form.application_name}
            onChange={(e) => setForm((f) => ({ ...f, application_name: e.target.value }))} fullWidth />
          <TextField label="Event name" value={form.event_name}
            onChange={(e) => setForm((f) => ({ ...f, event_name: e.target.value }))}
            helperText="e.g. customer, invoice, message" fullWidth />
          <TextField label="Event description" value={form.event_description}
            onChange={(e) => setForm((f) => ({ ...f, event_description: e.target.value }))} fullWidth />
          <FormControlLabel
            control={(
              <Switch
                checked={form.enabled}
                onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
              />
            )}
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : (editingId ? 'Save' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteConfirm)} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm delete</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete registry event {deleteConfirm?.application_name}/{deleteConfirm?.event_name}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button variant="contained" color="error" disabled={saving} onClick={() => handleDelete(deleteConfirm)}>
            {saving ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WebhookEventRegistryAdmin;
